import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { WizardNavigationButton } from '@/_shared/components/Wizard/types';
import { Routes } from '@/_shared/constants/routes';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { formatNumber } from '@/_shared/helpers/formatNumber';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { useWizard } from '@/_shared/hooks/useWizard';
import { CompletionResultStatusEnum } from '@/swagger/constants/quiz-enums';
import { getContentDetailsCall } from '@/training/hardSkills/state/api';
import { getQuizByIdResultCall } from '../../state';
import { DialogConfirmRestartQuiz } from '../DialogConfirmRestartQuiz';
import { DialogConfirmRestartQuizPayload } from '../DialogConfirmRestartQuiz/types';

type Props = { wizardNavigationButton: WizardNavigationButton };

export const QuizCompletedThankYouPage = ({ wizardNavigationButton }: Props) => {
  const t = useTranslations('quiz.dynamicQuiz');
  const { setCustomOnSkip, setCustomOnNext, resetWizardNavigationCallbacks } = useWizard();
  const { genericResponsiveValue } = useResponsiveStyle();
  const gapXXXL = convertSizeToNumber(`${useThemeValue('sizes', 'xxxl')}`) || 64;
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`) || 12;
  const { openDialog } = useDialog<DialogConfirmRestartQuizPayload>('confirmRestartQuizDialog');

  const { response: responseGetContentDetails } = getContentDetailsCall.hook();
  const { response: responseGetQuizByIdResult } = getQuizByIdResultCall.hook();

  const quizResult = useMemo(() => {
    return responseGetQuizByIdResult?.status ?? CompletionResultStatusEnum.NOT_SUCCEEDED;
  }, [responseGetQuizByIdResult]);

  const onClose = useCallback(() => {
    window.location.href = Routes.TrainingHardSkills;
  }, []);

  const onRestart: NonNullable<Parameters<typeof setCustomOnSkip>[0]> = useCallback(
    navigate => {
      openDialog({ navigate });
    },
    [openDialog],
  );

  useEffect(() => {
    setCustomOnSkip(onRestart);
    setCustomOnNext(onClose);

    return () => {
      // Cleanup function to reset the custom onNext callback
      resetWizardNavigationCallbacks();
    };
  }, [setCustomOnSkip, resetWizardNavigationCallbacks, onRestart, setCustomOnNext, onClose]);

  return (
    <Box>
      <Box
        display='flex'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        alignItems='flex-start'
        justifyContent='center'
        py='xl'
        width='100%'
        style={{ gap: gapXXXL }}
        flex={10}
      >
        <Box
          flex={genericResponsiveValue({
            breakPoints: { desktop: 5 },
            defaultValue: 10,
          })}
          marginY={genericResponsiveValue({
            breakPoints: { desktop: 'auto' },
            defaultValue: undefined,
          })}
        >
          <Typography
            variant='h2'
            color='primary/text'
            fontSize={50 as never}
            lineHeight={'60px' as never}
            fontWeight='bold'
            mb='xs'
          >
            {t('quizTitle', { quizName: responseGetContentDetails?.title })}
          </Typography>
          <Typography variant='p1/light' color='grey/light'>
            {responseGetContentDetails?.summary}
          </Typography>
        </Box>
        <Box
          flex={genericResponsiveValue({
            breakPoints: { desktop: 4 },
            defaultValue: 10,
          })}
        >
          <Card p='m'>
            <Box>
              <Typography variant='h1' color='primary/text' mb='xxs'>
                {t(`quizResult.${quizResult}.title`)}
              </Typography>
              <Box flexDirection='column' display='flex' my='s'>
                <Typography variant='special' color='primary/text'>
                  {formatNumber(Number(responseGetQuizByIdResult?.percentage?.toFixed(0)) ?? 0)}%
                </Typography>
                <Typography variant='p2' color='grey/light' fontSize='xs'>
                  {t('quizResult.correctAnswers', {
                    correct: responseGetQuizByIdResult?.corrects,
                    total: responseGetQuizByIdResult?.totals,
                  })}
                </Typography>
              </Box>
              {responseGetQuizByIdResult?.peerCount ? (
                <Typography variant='p3/light' color='primary/text' mb='m'>
                  {t(`quizResult.${quizResult}.label`, {
                    total: responseGetQuizByIdResult?.peerCount,
                    score: formatNumber(
                      Number(responseGetQuizByIdResult?.peerScore?.toFixed(0)) ?? 0,
                    ),
                  })}
                </Typography>
              ) : null}
              <Typography variant='p2' color='grey/light'>
                {t(`quizResult.${quizResult}.description`)}
              </Typography>
            </Box>
          </Card>
        </Box>
      </Box>
      <Box
        mt='xs'
        display='flex'
        flexDirection='row'
        justifyContent='flex-end'
        style={{ gap: gapXS }}
      >
        <Box display='flex'>
          <wizardNavigationButton.Skip />
        </Box>
        <Box display='flex'>
          <wizardNavigationButton.Next />
        </Box>
      </Box>
      <DialogConfirmRestartQuiz />
    </Box>
  );
};
